import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  ProjectGovernanceRole,
  type ProjectCommentParticipantDto,
  type ProjectGovernanceDto,
  type ProjectGovernanceRolesDto,
} from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useGovernanceMutation } from './useGovernanceMutation';

const NONE = '__none';
const ROLE_FIELDS: (keyof ProjectGovernanceRolesDto)[] = [
  'executiveUserId',
  'projectManagerUserId',
  'seniorUserUserId',
  'seniorSupplierUserId',
  'projectAssuranceUserId',
];

const ROLE_FIELD_TO_GOV: Record<keyof ProjectGovernanceRolesDto, ProjectGovernanceRole> = {
  executiveUserId: ProjectGovernanceRole.Executive,
  projectManagerUserId: ProjectGovernanceRole.ProjectManager,
  seniorUserUserId: ProjectGovernanceRole.SeniorUser,
  seniorSupplierUserId: ProjectGovernanceRole.SeniorSupplier,
  projectAssuranceUserId: ProjectGovernanceRole.ProjectAssurance,
};

export function GovernanceRolesForm({
  projectId,
  governance,
  participants,
  nameOf,
}: {
  projectId: string;
  governance: ProjectGovernanceDto;
  participants: ProjectCommentParticipantDto[];
  nameOf: (userId: string | null) => string;
}) {
  const { t } = useTranslation();
  const { permissions } = governance;
  const canEdit = (permissions.isBusinessLayer || permissions.isExecutive) && governance.state !== 'closed';

  const schema = yup
    .object({
      executiveUserId: yup.string().required(),
      projectManagerUserId: yup
        .string()
        .required()
        .test('pm-not-executive', t('workspace.governance.roles.pmIsExecutive'), function (value) {
          return value === NONE || value !== this.parent.executiveUserId;
        }),
      seniorUserUserId: yup.string().required(),
      seniorSupplierUserId: yup.string().required(),
      projectAssuranceUserId: yup
        .string()
        .required()
        .test('assurance-independent', t('workspace.governance.roles.assuranceConflict'), function (value) {
          return (
            value === NONE ||
            (value !== this.parent.executiveUserId && value !== this.parent.projectManagerUserId)
          );
        }),
    })
    .required();

  const { control, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema),
    values: Object.fromEntries(ROLE_FIELDS.map((f) => [f, governance.roles[f] ?? NONE])) as Record<
      keyof ProjectGovernanceRolesDto,
      string
    >,
  });

  const mutation = useGovernanceMutation(projectId, (roles: ProjectGovernanceRolesDto) =>
    projectGovernanceApi.assignRoles(projectId, { ...roles, expectedVersion: governance.version }),
  );

  const onSubmit = handleSubmit((values) => {
    const roles = Object.fromEntries(
      ROLE_FIELDS.map((f) => [f, values[f] === NONE ? null : values[f]]),
    ) as unknown as ProjectGovernanceRolesDto;
    mutation.mutate(roles);
  });

  if (!canEdit) {
    return (
      <dl className="grid grid-cols-2 gap-2 text-sm">
        {ROLE_FIELDS.map((f) => (
          <div key={f}>
            <dt className="text-xs text-muted-foreground">{t(`workspace.governance.roles.${f}`)}</dt>
            <dd>
              {nameOf(governance.roles[f])}
              {governance.vacantRoles.includes(ROLE_FIELD_TO_GOV[f]) && (
                <span className="ml-2 text-xs text-destructive">{t('workspace.governance.roles.vacant')}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {ROLE_FIELDS.map((f) => (
          <div key={f} className="space-y-1">
            <Label>{t(`workspace.governance.roles.${f}`)}</Label>
            <Controller
              control={control}
              name={f}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={f === 'executiveUserId' && !permissions.isBusinessLayer}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('workspace.governance.roles.none')}</SelectItem>
                    {participants
                      .filter((p) => f === 'seniorSupplierUserId' || p.accountSide === 'inquirer')
                      .map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.displayName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            />
            {governance.vacantRoles.includes(ROLE_FIELD_TO_GOV[f]) && (
              <p className="text-xs text-destructive">{t('workspace.governance.roles.vacant')}</p>
            )}
            {formState.errors[f] && <p className="text-xs text-destructive">{formState.errors[f]?.message}</p>}
          </div>
        ))}
      </div>
      <Button type="submit" size="sm" disabled={mutation.isPending}>
        {t('workspace.governance.roles.save')}
      </Button>
    </form>
  );
}
